import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { TooltipContainer, TooltipContent } from './Tooltip.style';

interface TooltipProps {
    text: React.ReactNode | string;
    children: React.ReactNode;
    type: 'information' | 'warning' | 'error';
    disabled?: boolean;
    forceVisible?: boolean;
}

interface TooltipPosition {
    top: number;
    left: number;
}

const Tooltip: React.FC<TooltipProps> = ({ text, children, type, disabled, forceVisible }) => {
    const [isHovered, setIsHovered] = useState(false);
    const [position, setPosition] = useState<TooltipPosition>({ top: 0, left: 0 });
    const triggerRef = useRef<HTMLDivElement>(null);
    const tooltipRef = useRef<HTMLDivElement>(null);

    // Create portal root if it doesn't exist
    const getOrCreatePortalRoot = () => {
        let portalRoot = document.getElementById('tooltip-root');
        if (!portalRoot) {
            portalRoot = document.createElement('div');
            portalRoot.id = 'tooltip-root';
            document.body.appendChild(portalRoot);
        }
        return portalRoot;
    };

    const handleMouseEnter = (event: React.MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        setIsHovered(true);
    };

    const handleMouseLeave = (event: React.MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        setIsHovered(false);
    };

    // Update tooltip position when it becomes visible or window is resized
    useEffect(() => {
        const updatePosition = () => {
            if (triggerRef.current && (isHovered || forceVisible) && !disabled) {
                const triggerRect = triggerRef.current.getBoundingClientRect();
                const tooltipHeight = tooltipRef.current?.offsetHeight || 0;

                const viewportHeight = window.innerHeight;
                const spaceBelow = viewportHeight - triggerRect.bottom;

                // Position the tooltip above the trigger if not enough space below
                if (spaceBelow < tooltipHeight + 10) {
                    setPosition({
                        top: triggerRect.top + window.scrollY - tooltipHeight - 5,
                        left: triggerRect.left + triggerRect.width / 2 + window.scrollX,
                    });
                } else {
                    // Position the tooltip below the trigger element
                    setPosition({
                        top: triggerRect.bottom + window.scrollY + 5,
                        left: triggerRect.left + triggerRect.width / 2 + window.scrollX,
                    });
                }
            }
        };

        updatePosition();

        // Update position on window resize
        window.addEventListener('resize', updatePosition);
        return () => {
            window.removeEventListener('resize', updatePosition);
        };
    }, [isHovered, forceVisible, disabled]);

    // Render tooltip content through portal
    const renderTooltipContent = () => {
        if ((isHovered || forceVisible) && !disabled) {
            const style = {
                transform: `translateX(-50%)`,
                top: `${position.top}px`,
                left: `${position.left}px`,
            };

            return ReactDOM.createPortal(
                <TooltipContent ref={tooltipRef} type={type} style={style}>
                    {text}
                </TooltipContent>,
                getOrCreatePortalRoot()
            );
        }
        return null;
    };

    return (
        <>
            <TooltipContainer ref={triggerRef} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
                {children}
            </TooltipContainer>
            {renderTooltipContent()}
        </>
    );
};

export default Tooltip;
